// libs
import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import { useTranslation } from "react-i18next";
import UserContext from "../../context/user-context";

// functions
import getDipReadingDevices from "../Utils/API/getDipReadingDevices";

// components
import Spinner from "../../components/UI/Spinner";
import Alert from "../../components/UI/Alerts/Alert";
import TankCard from "./TankCard";
import OrderPopup from "../Order/OrderPopup";
import SelectWrapper from "../Utils/Input/SelectWrapper";
import SearchForm from "../UI/SearchForm/SearchForm";
import Sorter from "../UI/Toolbar/Sorter";
import Pagination from "../UI/Pagination";
import {fireGTMOrderingEvent} from "../Utils/GTM/GTMHelper";


const TanksList = (props) => {
	const userContext = useContext(UserContext);
	const {t, i18n} = useTranslation();
	const { initialData, subAccounts, loading } = props;

	// State management ///////////////////////////////////////////////////////////////////////////////////////////////
	const [loadingDip,setLoadingDips] = useState(false); // Loading Dip devices
	const [show, setShow] = useState(false); // order popup
	const [currentTank, setCurrentTank] = useState([]); // used to populate the popup inner content
	const [tanks,setTanks] = useState([]); // Tanks to be displayed after filtering and such
	const [dipDevices, setDipDevices] = useState([]); //

	//filters & sort & search
	const initialFilters = {
		product: '',
		subAccountCode: '',
		productType: ''
	}
	const [productTypes, setProductTypes] = useState([]); // Manage the product type filter dropdown
	const [subAccountsFiltered, setSubAccountsFiltered] = useState([]); // Manage the subaccount filter dropdown
	const [hasSearched, setHasSearched] = useState(false); // useful for results title management
	const [filters, setFilters] = useState({...initialFilters}); // useful for filtering items

	const [countSort,setCountSort] = useState(0);

	const [sortField, setSortField] = useState('tankName');
	const [sortDirection, setSortDirection] = useState('ASC');

	const [pagination, setPagination] = useState({currentPage: 1, itemsPerPage: 6, totalPages: null}); /*6*/
	///////////////////////////////////////////////////////////////////////////////////////////////////////////////////

	const totalItems = (initialData && Array.isArray(initialData)) ? initialData.length : 0;
	const totalCurrentItems = (tanks && Array.isArray(tanks)) ? tanks.length : 0;

	const handleClose = () => {
		setShow(false);
	};

	const handleCloseNReload = (status) => {
		setShow(false);
		if(status === 'canceled'){
			fireGTMOrderingEvent('9_annule',{product : currentTank.product.descriptionFr});
		}
		props.reloadData(); // reset items to initial state
	};

	const handleShow = (tank) => {
		if(!tank) return null;
		fireGTMOrderingEvent('1_debute', {product : tank.product.descriptionFr});
		setCurrentTank(tank);
		setShow(true);
	};

	const onSearchDataUpdate = (searchData) => {
		if(Array.isArray(searchData)){
			setHasSearched(true);
			setTanks(searchData);
		}
	}

	const storeSortedItems = (sortedItems) => {
		if (sortedItems.length > 0) {
			setTanks(sortedItems);
		}
	}

	const handleSort = useCallback(sortBy => {
		if (sortBy.length > 0) {
			setSortField(sortBy[0].id);
			setSortDirection(sortBy[0].desc?'DESC':'ASC')
		}
	}, [sortField,sortDirection]);

	const onPageChanged = data => {
		const {currentPage, totalPages} = data;
		setPagination({
			...pagination,
			currentPage: currentPage,
			totalPages: totalPages,
		});
	}

	const initFilters = () => {
		if(initialData && Array.isArray(initialData) && initialData.length > 0){
			let tanksCopy = [...initialData];
			let types = tanksCopy.map(item => item.product);
			const typesFiltered = [...new Map(types.map(v => [v.code, v])).values()] // filter duplicates
			setProductTypes(typesFiltered);

			if(subAccounts && Array.isArray(subAccounts) && subAccounts.length > 0){
				const subAccountsFiltered = subAccounts.filter( subaccount => {
					let match = false;
					initialData.forEach(element => {
						if( !match && (element.subAccountCode === subaccount.subAccountCode) ){
							match = true;
						}
					});

					return match;
				})

				setSubAccountsFiltered(subAccountsFiltered);
			}
		}
	}

	const typeFilterHandler = (filter, value) => {
		const newFilters = { ...filters, [filter] : value }
		setFilters(newFilters);
	}

	const getIsFiltersEnabled = () => {
		let filtersEnabled = false;
		Object.values(filters).forEach(value => {
			filtersEnabled = ((value !== '' && value !== false) && !filtersEnabled) || filtersEnabled;
		});
		return filtersEnabled;
	}

	const filtering = useMemo(() => {
		if(!initialData) return [];
		let newItems = [...initialData];
		let filteredItems = newItems.filter(item => {
			let isMatching = true;
			Object.entries(filters).forEach(entry  => {
				const [key, value] = entry;
				if (value) {
					switch(key){
						case 'productType':
							isMatching = item.product.code === value && isMatching;
							break;
						case 'subAccountCode':
							isMatching = item.subAccountCode === value && isMatching;
							break;
						default: isMatching = true;
					}
				}
			})

			return isMatching;
		});

		return setTanks(filteredItems);
	}, [filters, initialData]);

	useEffect(() => {
		//if(!tanks || (Array.isArray(tanks) && !tanks.length)){
		if(initialData && Array.isArray(initialData) && initialData.length > 0 && !_.isEqual(tanks,initialData)){
			setTanks(initialData);
			setCountSort((prev) => prev+1)
		}
		//}
	}, [initialData]);

	useMemo(async () => {
		if (initialData && Array.isArray(initialData) && initialData.length > 0) {
			setLoadingDips(true);

			let tankCodes = [];
			initialData.forEach(element => {
				if (element.tankId) {
					tankCodes.push(element.tankId);
				}
			});

			if (tankCodes.length) {
				await getDipReadingDevices(tankCodes).then(r => {
					setDipDevices(r);
					setLoadingDips(false);
				});
			} else {
				setLoadingDips(false);
			}
		}
	}, [initialData]);

	useMemo( () => initFilters(), [initialData]);

	let title = "";
	if (totalItems > 1 || totalCurrentItems > 1) {
		if (getIsFiltersEnabled() || hasSearched) title = totalCurrentItems + ' ' + t('tanks.title_plural_lowercase_criteria');
		else title = totalItems + ' ' + t('tanks.title_plural_lowercase');
	} else {
		if (getIsFiltersEnabled() || hasSearched) title = totalCurrentItems + ' ' + t('tanks.title_singular_lowercase_criteria');
		else title = totalItems + ' ' + t('tanks.title_singular_lowercase');
	}

	let tanksHtml = '';
	if (loading) {
		tanksHtml = <Spinner/>;
	} else {
		if(tanks && tanks.length){
			let pageLimit = pagination.itemsPerPage;
			let currentPage = pagination.currentPage;
			let offset = (currentPage - 1) * pageLimit;
			let itemsPaged = tanks.slice(offset, offset + pageLimit);
			const hasDipDevices = (!loadingDip && dipDevices && dipDevices.length);

			tanksHtml = (
				<>
					{ (hasDipDevices) ?
						(
							<div className="messages">
								<Alert type="warning" item={{
									type: '',
									additionalClasses: '',
									customIcon: 'fas fa-clock',
									text: 'tanks.notice_delay'
								}}/>
							</div>
						) : ''
					}
					<div className="row">
						{ itemsPaged.map( (tank, i) => {
							let currentTankReadingDevice = null;
							if(!loadingDip && dipDevices && Array.isArray(dipDevices) && dipDevices.length){
								currentTankReadingDevice = dipDevices.filter(obj => {
									return obj.tankNumber === tank.tankId;
								})[0];
							}
							let currentSubaccount = null;
							if(props.subAccount){
								currentSubaccount = props.subAccount;
							} else if(subAccounts && Array.isArray(subAccounts) && subAccounts.length) {
								currentSubaccount = subAccounts.filter(obj => {
									return obj.subAccountCode === tank.subAccountCode;
								})[0];
							}

							return <div className="col-12 col-sm-6 col-md-4 mb-4" key={`tank-${tank.tankId}-${i}`}>
								<TankCard tank={tank}
										  subAccountName={ (currentSubaccount) ? currentSubaccount.subAccountName : null }
										  subAccountCode={ (currentSubaccount) ? currentSubaccount.subAccountCode : null }
										  onOrderClick={ handleShow }
										  loadingDip={ loadingDip }
										  dipDevice={ currentTankReadingDevice }
										  refreshTabs={ (props.hasOwnProperty('refreshTabs')) ? props.refreshTabs : null }
										  changeDateFilter={ (props.hasOwnProperty('changeDateFilter')) ? props.changeDateFilter : null }
								/>
							</div>
						}) }
					</div>
				</>
			);
		} else {
			tanksHtml = (
				<Alert type="info" item={{
					type: 'info',
					additionalClasses: 'message--condensed',
					customIcon: 'fas fa-question-circle',
					text: 'tanks.noresults'
				}}/>
			)
		}
	}

	let orderPopup = '';
	if(!loading && show && currentTank){
		const orderPopupProps = {
			show : show,
			onHide : handleClose,
			onHideNReload : handleCloseNReload,
			currentTank : currentTank
		}

		if(props.subAccount) {
			orderPopup = <OrderPopup {...orderPopupProps} subAccount={props.subAccount} />
		} else {
			orderPopup = <OrderPopup {...orderPopupProps} subAccount={null} />
		}

	}

	return (
		<>
			<section className='section section--youraccount section--padding--top--20 flex section--youraccount--notdash'>
				<div className="account-row">
					<SearchForm data={initialData} currentData={tanks} updateData={onSearchDataUpdate} />
				</div>
				<div className="flex align-items-center row section--padding--bottom--20 filters">
					<div className="col-lg-7 filters__left">
						<strong>{t('misc.filter')}&nbsp;&nbsp;</strong>
						{ (productTypes && Array.isArray(productTypes) && productTypes.length > 0) ? (
							<div className="mb-3 mb-sm-0 mr-2">
								<SelectWrapper>
									<select
										onChange={(e) => {
											let selectValue = e.target.value;
											typeFilterHandler('productType', selectValue);
										}}
										value={ filters.productType }
									>
										<option value="">{t('tanks.filters.product_type')}</option>
										{productTypes.map(type => {
											if (type.code) {
												return (
													<option value={type.code} key={`product-type-option-${type.code}`}>
														{ i18n.language === "fr"  ? type.descriptionFr : type.descriptionEn }
													</option>
												);
											} else {
												return '';
											}
										})}
									</select>
								</SelectWrapper>
							</div>
						) : ''}
						{ (subAccountsFiltered && Array.isArray(subAccountsFiltered) && subAccountsFiltered.length > 0) ? (
							<div className="mb-3 mb-sm-0 mr-2">
								<SelectWrapper>
									<select
										onChange={(e) => {
											let selectValue = e.target.value;
											typeFilterHandler('subAccountCode', selectValue);
										}}
										value={ filters.subAccountCode }
									>
										<option value="">{t('misc.account')}</option>
										{subAccountsFiltered.map(subaccount => {
											if (subaccount.subAccountCode) {
												return (
													<option value={subaccount.subAccountCode} key={`option-subaccount-code-${subaccount.subAccountCode}`}>
														{ subaccount.subAccountName + ' (' + subaccount.subAccountCode + ')' }
													</option>
												);
											} else {
												return '';
											}
										})}
									</select>
								</SelectWrapper>
							</div>
						) : ''}
					</div>
					<div className="col-lg-5 filters__right">
						<Sorter
							sortOptions={[
								{
									label: t('profile.account_code'),
									id: 'subAccountCode',
									value: 'subAccountCode',
									type: 'int'
								}, {
									label: t('tanks.filters.tank_name'),
									id: 'tankName',
									value: 'tankName',
									type: 'string'
								}, {
									label: t('tanks.filters.product_type'),
									id: 'product',
									value: 'product.code',
									type: 'string'
								}
							]}
							items={tanks}
							getSortedItems={storeSortedItems}
							sortDirection={sortDirection}
							sortField={sortField}
							onSort={handleSort}
							countSort={countSort}
						/>
					</div>
				</div>
				{ (!loading) ? (
					<h3 className="headline headline--block">
						{ title }
					</h3>
				) : '' }
			</section>

			{ tanksHtml }

			{
				(totalCurrentItems > pagination.itemsPerPage) ? (
					<Pagination totalRecords={totalCurrentItems}
								pageLimit={pagination.itemsPerPage}
								pageNeighbours={1}
								onPageChanged={onPageChanged}/>
				) : ""
			}

			{ orderPopup }
		</>
	);
};

export default TanksList;
